import { useState, useEffect, useRef } from 'react';
import { Link, useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Flame, Shirt, CircleDot, Lamp, BookOpen, Landmark, Gift,
  ChevronRight, Star, ShoppingCart, Loader2, Sparkles,
  Activity, Home, Gem, Palette, Music, PenTool, Heart,
  BadgeCheck, MapPin, Hammer, Flag, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/shop.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryIcons = {
  'flame': Flame, 'shirt': Shirt, 'circle-dot': CircleDot, 'lamp': Lamp,
  'book-open': BookOpen, 'landmark': Landmark, 'gift': Gift,
  'activity': Activity, 'home': Home, 'gem': Gem, 'palette': Palette,
  'sparkles': Sparkles, 'music': Music, 'pen': PenTool, 'heart': Heart,
};

const fallbackImage = 'https://images.unsplash.com/photo-1633617127680-18229ebb1b94?w=400&fit=crop';

const HERO_SLIDES = [
  {
    id: 'sw-1',
    tag: 'SWADESHI MARKETPLACE',
    title: '86 Categories of Sacred Essentials',
    subtitle: 'Pooja, rudraksha, murtis, books & more',
    cta: 'Explore All',
    href: '/shop/category/all',
    bg: 'linear-gradient(135deg,#9a3412 0%,#ea580c 60%,#f59e0b 100%)',
    image: 'https://images.pexels.com/photos/7351728/pexels-photo-7351728.jpeg?w=900&fit=crop',
  },
  {
    id: 'sw-2',
    tag: 'FESTIVAL SPECIAL',
    title: 'Diwali & Pooja Essentials',
    subtitle: 'Diyas, hawan samagri, rangoli kits',
    cta: 'Shop Festive',
    href: '/shop/category/cat_gifts',
    bg: 'linear-gradient(135deg,#7c2d12 0%,#b45309 50%,#fbbf24 100%)',
    image: 'https://images.pexels.com/photos/6315702/pexels-photo-6315702.jpeg?w=900&fit=crop',
  },
  {
    id: 'sw-3',
    tag: 'BLESSED & AUTHENTIC',
    title: 'Rudraksha & Tulsi Mala',
    subtitle: 'Energised at trusted temples',
    cta: 'Shop Mala',
    href: '/shop/category/cat_mala_rudraksha',
    bg: 'linear-gradient(135deg,#581c87 0%,#9333ea 50%,#ec4899 100%)',
    image: 'https://images.unsplash.com/photo-1624862301587-9f3900f0b8d1?w=900&fit=crop',
  },
];

const FEATURED_PARENT_IDS = [
  'cat_pooja_samagri',
  'cat_mala_rudraksha',
  'cat_idols_statues',
  'cat_spiritual_books',
];

// ──────────────────────────────────────────────────────────────────────────
// FK Hero Carousel
// ──────────────────────────────────────────────────────────────────────────
const FKHero = () => {
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    timer.current = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 4500);
    return () => clearInterval(timer.current);
  }, []);

  const slide = HERO_SLIDES[idx];

  return (
    <div className="shop-fk-hero" data-testid="hero-carousel">
      <div className="shop-fk-hero-slide" style={{ background: slide.bg }}>
        <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <span className="shop-fk-hero-tag">{slide.tag}</span>
          <h1 className="shop-fk-hero-title">{slide.title}</h1>
          <p className="shop-fk-hero-sub">{slide.subtitle}</p>
          <Link to={slide.href} className="shop-fk-hero-cta" data-testid={`hero-cta-${slide.id}`}>
            {slide.cta} <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <img
          src={slide.image}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute', right: 0, top: 0, height: '100%', width: '60%',
            objectFit: 'cover', opacity: 0.55, mixBlendMode: 'overlay',
          }}
        />
      </div>
      <div className="shop-fk-hero-dots">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.id}
            className={`shop-fk-hero-dot ${i === idx ? 'is-active' : ''}`}
            onClick={() => { clearInterval(timer.current); setIdx(i); }}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// FK Quick Categories (4-col grid wraps on mobile)
// ──────────────────────────────────────────────────────────────────────────
const FKQuickCats = ({ parents }) => (
  <div className="shop-fk-section" data-testid="quick-categories">
    <div className="shop-fk-quickcats">
      {parents.slice(0, 8).map((c) => {
        const Icon = categoryIcons[c.icon] || Flame;
        return (
          <Link
            to={`/shop/category/${c.id}`}
            key={c.id}
            className="shop-fk-quickcat"
            data-testid={`quick-cat-${c.id}`}
          >
            {c.image ? (
              <img src={c.image} alt={c.name} className="shop-fk-quickcat-img" />
            ) : (
              <div className="shop-fk-quickcat-icon"><Icon className="w-6 h-6" /></div>
            )}
            <span className="shop-fk-quickcat-label">{c.name}</span>
          </Link>
        );
      })}
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────────────
// FK Product Card (2-column) with wishlist + Added ✓ interaction
// ──────────────────────────────────────────────────────────────────────────
const FKProductCard = ({ product, onAddToCart, loading, isAdded, isWished, onToggleWishlist }) => {
  const hasDiscount = product.original_price && product.original_price > product.price;
  const discountPercent = hasDiscount ? Math.round((1 - product.price / product.original_price) * 100) : 0;

  return (
    <div className="shop-fk-card" data-testid={`product-card-${product.id}`}>
      <Link to={`/shop/product/${product.id}`} className="block">
        <div className="shop-fk-card-imgwrap">
          <img
            src={product.images?.[0] || fallbackImage}
            alt={product.name}
            className="shop-fk-card-img"
            loading="lazy"
          />
          {hasDiscount && <span className="shop-fk-card-badge-discount">{discountPercent}% OFF</span>}
          {product.is_featured && (
            <span className="shop-fk-card-badge-featured">
              <Sparkles className="w-2.5 h-2.5" /> Top
            </span>
          )}
          {product.is_swadeshi && (
            <span className="shop-sw-badge" data-testid={`swadeshi-badge-${product.id}`}>
              <span className="flag" aria-hidden="true">🇮🇳</span>
              MADE IN INDIA
            </span>
          )}
          <button
            type="button"
            className={`shop-fk-card-wishlist ${isWished ? 'is-active' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleWishlist(product.id); }}
            aria-label={isWished ? 'Remove from wishlist' : 'Add to wishlist'}
            data-testid={`wishlist-${product.id}`}
          >
            <Heart className="w-4 h-4" fill={isWished ? 'currentColor' : 'none'} />
          </button>
        </div>
      </Link>

      <div className="shop-fk-card-body">
        <Link to={`/shop/product/${product.id}`} className="block" title={product.name}>
          <h3 className="shop-fk-card-name">{product.name}</h3>
        </Link>

        {product.rating > 0 && (
          <div className="shop-fk-card-rating-row">
            <span className="shop-fk-card-rating-pill">
              <Star className="w-2.5 h-2.5 fill-current" />
              {product.rating}
            </span>
            <span className="shop-fk-card-rating-count">({product.reviews_count || 0})</span>
          </div>
        )}

        <div className="shop-fk-card-price-row">
          <span className="shop-fk-card-price">₹{product.price}</span>
          {hasDiscount && <span className="shop-fk-card-mrp">₹{product.original_price}</span>}
          {hasDiscount && <span className="shop-fk-card-disc">{discountPercent}% off</span>}
        </div>

        {product.price >= 500 && (
          <span className="shop-fk-card-bank">Free Delivery</span>
        )}

        {product.is_swadeshi && product.artisan_name && (
          <div className="shop-sw-artisan" data-testid={`artisan-${product.id}`}>
            <Hammer className="w-3 h-3 shop-sw-artisan-icon" />
            <span className="truncate">{product.artisan_name} · {product.artisan_location}</span>
          </div>
        )}

        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAddToCart(product.id); }}
          disabled={loading || product.stock === 0 || isAdded}
          className={`shop-fk-card-add ${isAdded ? 'is-added' : ''}`}
          data-testid={`add-to-cart-${product.id}`}
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : isAdded ? (
            <><Check className="w-3.5 h-3.5" /> Added ✓</>
          ) : (
            <><ShoppingCart className="w-3.5 h-3.5" />{product.stock === 0 ? 'Sold Out' : 'Add to Cart'}</>
          )}
        </button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// FK Horizontal "In Demand" strip
// ──────────────────────────────────────────────────────────────────────────
const FKHStrip = ({ products }) => {
  if (!products?.length) return null;
  return (
    <div className="shop-fk-hstrip" data-testid="hstrip-in-demand">
      {products.map((p) => (
        <Link
          key={p.id}
          to={`/shop/product/${p.id}`}
          className="shop-fk-hstrip-card"
          data-testid={`hstrip-card-${p.id}`}
        >
          <img
            src={p.images?.[0] || fallbackImage}
            alt={p.name}
            className="shop-fk-hstrip-img"
            loading="lazy"
          />
          <div className="shop-fk-hstrip-title">{p.name}</div>
          <div className="shop-fk-hstrip-price">₹{p.price}</div>
        </Link>
      ))}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// FK Per-Category Section (header + 2-col grid)
// ──────────────────────────────────────────────────────────────────────────
const FKCategorySection = ({ category, products, onAddToCart, addingToCart, addedIds, wishedIds, onToggleWishlist }) => {
  if (!products?.length) return null;

  return (
    <section className="shop-fk-section" data-testid={`cat-strip-${category.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="shop-fk-section-title">{category.name}</h2>
          {category.name_hindi && (
            <p className="shop-fk-section-subtitle">{category.name_hindi}</p>
          )}
        </div>
        <Link to={`/shop/category/${category.id}`} className="shop-fk-view-all" data-testid={`view-all-${category.id}`}>
          VIEW ALL <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="shop-fk-grid">
        {products.slice(0, 4).map((p) => (
          <FKProductCard
            key={p.id}
            product={p}
            onAddToCart={onAddToCart}
            loading={addingToCart === p.id}
            isAdded={addedIds.has(p.id)}
            isWished={wishedIds.has(p.id)}
            onToggleWishlist={onToggleWishlist}
          />
        ))}
      </div>
    </section>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// 🇮🇳 Swadeshi Banner Block — Made in India hero
// ──────────────────────────────────────────────────────────────────────────
const SwadeshiBanner = ({ subCats, swadeshiOnly, onToggleSwadeshi }) => (
  <section className="shop-fk-section" data-testid="swadeshi-section" style={{ background: '#fff' }}>
    <div className="shop-sw-banner">
      <div className="shop-sw-banner-row">
        <div className="shop-sw-flag" aria-hidden="true">🇮🇳</div>
        <div>
          <h2 className="shop-sw-banner-title">Swadeshi (Made in India)</h2>
          <p className="shop-sw-banner-tagline">Apne desh ka, apni parampara ka</p>
        </div>
      </div>

      <div className="shop-sw-banner-pillrow">
        <span className="shop-sw-banner-pill"><BadgeCheck className="w-3 h-3" /> 100% Verified</span>
        <span className="shop-sw-banner-pill"><Hammer className="w-3 h-3" /> Artisan-made</span>
        <span className="shop-sw-banner-pill"><Heart className="w-3 h-3" /> Supports gau-seva</span>
      </div>

      <Link to="/shop/category/cat_swadeshi" className="shop-sw-banner-cta" data-testid="swadeshi-explore-cta">
        Explore Swadeshi <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>

    {/* 8 sub-category icons */}
    {subCats?.length > 0 && (
      <div className="shop-sw-subcats" data-testid="swadeshi-subcats">
        {subCats.map((c) => (
          <Link
            to={`/shop/category/${c.id}`}
            key={c.id}
            className="shop-sw-subcat"
            data-testid={`sw-subcat-${c.id}`}
          >
            <img
              src={c.image || fallbackImage}
              alt={c.name}
              className="shop-sw-subcat-img"
              loading="lazy"
            />
            <span className="shop-sw-subcat-label">{c.name.replace('Swadeshi ', '').replace(' & Decor', '')}</span>
          </Link>
        ))}
      </div>
    )}

    {/* Toggle filter */}
    <div className="flex justify-center mt-4">
      <button
        type="button"
        className={`shop-sw-filter ${swadeshiOnly ? 'is-active' : ''}`}
        onClick={onToggleSwadeshi}
        data-testid="swadeshi-only-toggle"
      >
        <span className="switch" aria-hidden="true" />
        <Flag className="w-3.5 h-3.5" />
        Show only Made in India
      </button>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────
export default function ShopHomePage() {
  const { user, refreshCart } = useOutletContext();
  const navigate = useNavigate();
  const [allCategories, setAllCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [swadeshiSubCats, setSwadeshiSubCats] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [swadeshiProducts, setSwadeshiProducts] = useState([]);
  const [stripsByCat, setStripsByCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);
  const [addedIds, setAddedIds] = useState(() => new Set());
  const [wishedIds, setWishedIds] = useState(() => {
    try {
      const raw = localStorage.getItem('dharmshop_wishlist');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const [swadeshiOnly, setSwadeshiOnly] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, featuredRes, allRes, swadeshiRes] = await Promise.all([
        axios.get(`${API}/shop/categories`),
        axios.get(`${API}/shop/products/featured?limit=10`),
        axios.get(`${API}/shop/products?limit=12`),
        axios.get(`${API}/shop/products?swadeshi_only=true&limit=12`),
      ]);
      const cats = categoriesRes.data || [];
      setAllCategories(cats);
      // parent categories (skip the swadeshi parent in the regular FK strip — it has its own block)
      setParentCategories(cats.filter((c) => !c.parent_id && c.id !== 'cat_swadeshi'));
      setSwadeshiSubCats(cats.filter((c) => c.parent_id === 'cat_swadeshi'));
      setFeaturedProducts(featuredRes.data || []);
      setAllProducts(allRes.data || []);
      setSwadeshiProducts(swadeshiRes.data || []);

      const stripPromises = FEATURED_PARENT_IDS.map((cid) =>
        axios.get(`${API}/shop/products?category_id=${cid}&limit=4`)
          .then((r) => [cid, r.data]).catch(() => [cid, []])
      );
      const stripResults = await Promise.all(stripPromises);
      const stripMap = {};
      stripResults.forEach(([cid, prods]) => { stripMap[cid] = prods; });
      setStripsByCat(stripMap);
    } catch (error) {
      toast.error('Failed to load shop data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (productId) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/auth');
      return;
    }
    setAddingToCart(productId);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/shop/cart`,
        { product_id: productId, quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Added to cart!');
      refreshCart();
      // Mark added — keep button green for 3s
      setAddedIds((prev) => new Set([...prev, productId]));
      setTimeout(() => {
        setAddedIds((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  const handleToggleWishlist = (productId) => {
    setWishedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
        toast('Removed from wishlist');
      } else {
        next.add(productId);
        toast.success('Added to wishlist ❤️');
      }
      try { localStorage.setItem('dharmshop_wishlist', JSON.stringify([...next])); } catch (_) { /* ignore */ }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <Lamp className="w-10 h-10 text-amber-500 animate-pulse" />
        <p className="text-amber-700 font-medium text-sm">Loading sacred items...</p>
      </div>
    );
  }

  // Apply Swadeshi-only filter to display lists
  const filterSw = (arr) => (swadeshiOnly ? arr.filter((p) => p.is_swadeshi) : arr);
  const visibleFeatured = filterSw(featuredProducts);
  const visibleAll = filterSw(allProducts);

  return (
    <div className="shop-page" data-testid="shop-home-page">
      {/* Hero */}
      <div className="shop-fk-section" style={{ marginTop: 0, padding: 8 }}>
        <FKHero />
      </div>

      {/* 🇮🇳 Swadeshi (Made in India) banner — placed above fold for trust */}
      <SwadeshiBanner
        subCats={swadeshiSubCats}
        swadeshiOnly={swadeshiOnly}
        onToggleSwadeshi={() => setSwadeshiOnly((v) => !v)}
      />

      {/* Quick Categories */}
      <FKQuickCats parents={parentCategories} />

      {/* Promo row */}
      <section className="shop-fk-section" data-testid="featured-products-section">
        <div className="shop-fk-promo-row">
          <Link to="/shop/category/cat_diyas_candles" className="shop-fk-promo-card a" data-testid="promo-diwali">
            <div className="shop-fk-promo-tag">Festival Special</div>
            <div className="shop-fk-promo-title">Diwali Essentials</div>
            <div className="shop-fk-promo-sub">Diyas, candles & decor</div>
          </Link>
          <Link to="/shop/category/cat_swadesi_beauty" className="shop-fk-promo-card b" data-testid="promo-swadeshi">
            <div className="shop-fk-promo-tag">Swadeshi Beauty</div>
            <div className="shop-fk-promo-title">Ayurveda & Ubtan</div>
            <div className="shop-fk-promo-sub">Natural skincare</div>
          </Link>
        </div>
      </section>

      {/* Swadeshi product strip — always shown, even when filter off */}
      {swadeshiProducts.length > 0 && (
        <section className="shop-fk-section" data-testid="swadeshi-products-section">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="shop-fk-section-title">
                <span aria-hidden="true">🇮🇳</span> Made in India by artisans
              </h2>
              <p className="shop-fk-section-subtitle">Handmade · authentic · supports Bharat</p>
            </div>
            <Link to="/shop/category/cat_swadeshi" className="shop-fk-view-all">
              VIEW ALL <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <FKHStrip products={swadeshiProducts} />
        </section>
      )}

      {/* In Demand horizontal strip (featured products) */}
      {visibleFeatured.length > 0 && (
        <section className="shop-fk-section" data-testid="in-demand-section">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="shop-fk-section-title">In demand</h2>
              <p className="shop-fk-section-subtitle">Most loved by devotees</p>
            </div>
            <Link to="/shop/category/all" className="shop-fk-view-all">
              VIEW ALL <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <FKHStrip products={visibleFeatured} />
        </section>
      )}

      {/* Per-Category Sections (4 strips) */}
      {FEATURED_PARENT_IDS.map((cid) => {
        const cat = allCategories.find((c) => c.id === cid);
        if (!cat) return null;
        const prods = filterSw(stripsByCat[cid] || []);
        return (
          <FKCategorySection
            key={cid}
            category={cat}
            products={prods}
            onAddToCart={handleAddToCart}
            addingToCart={addingToCart}
            addedIds={addedIds}
            wishedIds={wishedIds}
            onToggleWishlist={handleToggleWishlist}
          />
        );
      })}

      {/* Popular - 2-col grid */}
      {visibleAll.length > 0 && (
        <section className="shop-fk-section" data-testid="popular-products-section">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="shop-fk-section-title">Popular in DharmShop</h2>
              <p className="shop-fk-section-subtitle">Trending picks for you</p>
            </div>
            <Link to="/shop/category/all" className="shop-fk-view-all">
              SEE MORE <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="shop-fk-grid">
            {visibleAll.slice(0, 8).map((p) => (
              <FKProductCard
                key={p.id}
                product={p}
                onAddToCart={handleAddToCart}
                loading={addingToCart === p.id}
                isAdded={addedIds.has(p.id)}
                isWished={wishedIds.has(p.id)}
                onToggleWishlist={handleToggleWishlist}
              />
            ))}
          </div>
        </section>
      )}

      {/* Bottom spacer for mobile bottom nav */}
      <div style={{ height: 80 }} />
    </div>
  );
}
